import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { downloadXlsx } from '../utils/exportXlsx';
import ReportShell from './ReportShell';

// Daily Esri completions — tickets the team marked PROGRESS = COMPLETE on a day (or
// range), with who did them (PM1). "When" is the first 15-minute poll that saw
// COMPLETE, since the layer itself doesn't record edit times. Yellow rows: Esri says
// complete but the city hasn't responded in WA 811 yet.

const SURFACE = '#0d1b2e';
const BORDER = '#1e3a5f';
const inputStyle = { backgroundColor: SURFACE, borderColor: BORDER };
const CITIES = [
  { key: 'STEILACOOM', label: 'Steilacoom' },
  { key: 'FIFE', label: 'Fife' },
  { key: 'ALGONA', label: 'Algona' },
];
const YELLOW = { row: 'rgba(250,204,21,0.14)', edge: '#facc15', text: '#fde68a' };

const TZ = 'America/Los_Angeles';
const todayPacific = () => new Date().toLocaleDateString('en-CA', { timeZone: TZ });
const parseDay = (s) => new Date(`${s}T00:00:00Z`);
const addDays = (s, n) => new Date(parseDay(s).getTime() + n * 86400000).toISOString().slice(0, 10);
const mondayOf = (s) => addDays(s, -((parseDay(s).getUTCDay() + 6) % 7));
const fmtDay = (s) => { if (!s) return '—'; const [y, m, d] = s.split('-'); return `${+m}/${+d}/${y}`; };
const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-US', { timeZone: TZ, month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { timeZone: TZ }) : '—');

function presets() {
  const t = todayPacific();
  const mon = mondayOf(t);
  return {
    Today: [t, t],
    Yesterday: [addDays(t, -1), addDays(t, -1)],
    'This week': [mon, t],
    'Last week': [addDays(mon, -7), addDays(mon, -1)],
  };
}

export default function EsriDailyReport({ onClose, embedded = false }) {
  const [from, setFrom] = useState(todayPacific());
  const [to, setTo] = useState(todayPacific());
  const [city, setCity] = useState('all');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function run(f = from, t = to, c = city) {
    if (!f || !t || f > t) { setError('Pick a start date on or before the end date.'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/tickets/esri-completed', { params: { from: f, to: t, city: c } });
      setReport(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not build the report.');
    } finally {
      setLoading(false);
    }
  }

  // Run once on open (today).
  useEffect(() => { run(); }, []);

  const rows = report?.rows || [];
  const shownCities = city === 'all' ? CITIES : CITIES.filter((c) => c.key === city);
  const byCity = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.city] = (m[r.city] || 0) + 1; });
    return m;
  }, [rows]);
  const byPm = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const k = (r.pm1 || '').trim() || '(not filled in)';
      m[k] = m[k] || {};
      m[k][r.city] = (m[k][r.city] || 0) + 1;
    });
    return Object.entries(m).sort((a, b) => total(b[1]) - total(a[1]));
  }, [rows]);
  const conflicts = rows.filter((r) => r.conflict).length;
  const oneDay = report && report.from === report.to;

  async function exportExcel() {
    await downloadXlsx({
      filename: `811-esri-completed-${report.from}${oneDay ? '' : `-to-${report.to}`}${city !== 'all' ? `-${city.toLowerCase()}` : ''}.xlsx`,
      sheetName: 'Esri Completed',
      headers: ['Marked Complete (Esri)', 'Ticket #', 'City (Locate For)', 'Ticket Place', 'House #', 'Street', 'Intersecting Street', 'Completed By (PM1)', '811 Status (City)', 'City Responded', 'Called In', 'Contractor', 'Flag', 'Ticket Link'],
      rows: rows.map((r) => [
        fmtTime(r.esri_completed_at), r.ticket_no, r.city, r.place || '', r.address || '', r.street || '', r.cross_street || '',
        r.pm1 || '', r.city_status || '', r.city_completed_at ? fmtTime(r.city_completed_at) : '',
        fmtDate(r.call_date), r.excavator || '', r.conflict ? 'City has not responded in WA 811' : '', r.ticket_link || '',
      ]),
    });
  }

  return (
    <ReportShell embedded={embedded} onClose={onClose} title="Daily Esri Completions" maxWidth="max-w-6xl"
      subtitle="Tickets marked PROGRESS = COMPLETE in Esri, and who completed them (PM1)">
      <div className="flex items-center gap-3 px-5 pt-4 pb-3 flex-wrap">
        <label className="text-xs text-gray-400 uppercase tracking-wide">From</label>
        <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="text-sm rounded px-2 py-1.5 border text-white focus:outline-none [color-scheme:dark]" style={inputStyle} />
        <label className="text-xs text-gray-400 uppercase tracking-wide">To</label>
        <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="text-sm rounded px-2 py-1.5 border text-white focus:outline-none [color-scheme:dark]" style={inputStyle} />
        <select value={city} onChange={(e) => setCity(e.target.value)} className="text-sm rounded px-2 py-1.5 border text-white focus:outline-none" style={inputStyle}>
          <option value="all">All cities</option>
          {CITIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button
          onClick={() => run()}
          disabled={loading}
          className="text-xs px-3 py-2 rounded font-bold uppercase tracking-wide transition disabled:opacity-50"
          style={{ backgroundColor: '#c9a84c', color: SURFACE }}
        >
          {loading ? 'Running…' : 'Run report'}
        </button>
        <button
          onClick={exportExcel}
          disabled={!rows.length}
          className="text-xs px-3 py-2 rounded border font-bold uppercase tracking-wide text-gray-300 hover:text-white transition disabled:opacity-40"
          style={{ borderColor: BORDER }}
        >
          Export to Excel
        </button>
        <div className="flex gap-1 ml-auto">
          {Object.entries(presets()).map(([name, [f, t]]) => (
            <button
              key={name}
              onClick={() => { setFrom(f); setTo(t); run(f, t); }}
              className="text-[11px] px-2 py-1 rounded border text-gray-400 hover:text-white transition"
              style={{ borderColor: BORDER }}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-y-auto px-5 pb-5 space-y-4">
        {error && <p className="text-red-400 text-sm">{error}</p>}
        {report?.layerError && <p className="text-yellow-300 text-xs">⚠ {report.layerError}</p>}

        {report && (
          <>
            <div className="flex flex-wrap gap-3">
              <Stat label={oneDay ? `Completed ${fmtDay(report.from)}` : `Completed ${fmtDay(report.from)} – ${fmtDay(report.to)}`} value={rows.length} />
              {shownCities.map((c) => <Stat key={c.key} label={c.label} value={byCity[c.key] || 0} />)}
              {conflicts > 0 && (
                <div className="rounded-lg border px-3 py-2" style={{ borderColor: YELLOW.edge, backgroundColor: YELLOW.row }}>
                  <div className="text-[11px] uppercase tracking-wide" style={{ color: YELLOW.text }}>■ Yellow — city hasn't responded yet</div>
                  <div className="text-2xl font-black text-white tabular-nums">{conflicts}</div>
                </div>
              )}
            </div>

            {report.trackingStarted && (
              <p className="text-xs text-gray-500">
                Completion times are tracked from {fmtTime(report.trackingStarted)} (checked every 15 minutes).
                {report.completedBeforeTracking > 0 && ` ${report.completedBeforeTracking} ticket${report.completedBeforeTracking === 1 ? ' was' : 's were'} already COMPLETE before then and ${report.completedBeforeTracking === 1 ? "isn't" : "aren't"} counted on any day.`}
              </p>
            )}

            {rows.length > 0 && (
              <section>
                <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2">Completed by (PM1)</h3>
                <table className="text-xs">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="text-left font-semibold pr-6 pb-1">PM1</th>
                      {shownCities.map((c) => <th key={c.key} className="text-right font-semibold px-3 pb-1">{c.label}</th>)}
                      <th className="text-right font-semibold pl-3 pb-1">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byPm.map(([pm, counts]) => (
                      <tr key={pm} className="border-t" style={{ borderColor: BORDER }}>
                        <td className={`pr-6 py-1 ${pm.startsWith('(') ? 'text-gray-500 italic' : 'text-white'}`}>{pm}</td>
                        {shownCities.map((c) => <td key={c.key} className="text-right px-3 py-1 text-gray-300 tabular-nums">{counts[c.key] || 0}</td>)}
                        <td className="text-right pl-3 py-1 text-white font-semibold tabular-nums">{total(counts)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            <section>
              <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2">Tickets ({rows.length})</h3>
              {rows.length === 0 ? (
                <p className="text-gray-500 text-sm">No tickets were marked COMPLETE in Esri {oneDay ? 'on this day' : 'in this range'}.</p>
              ) : (
                <div className="rounded-lg border overflow-x-auto" style={{ borderColor: BORDER }}>
                  <table className="w-full text-xs">
                    <thead style={{ backgroundColor: '#080f1a' }}>
                      <tr>
                        {['Marked Complete', 'Ticket #', 'City', 'Address', 'Completed By (PM1)', '811 Status (City)', 'City Responded', 'Called In', 'Contractor'].map((h) => (
                          <th key={h} className="text-left px-3 py-2 font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.ticket_no} className="border-t hover:bg-white/5" style={r.conflict
                          ? { backgroundColor: YELLOW.row, borderColor: BORDER, boxShadow: `inset 3px 0 0 ${YELLOW.edge}` }
                          : { borderColor: BORDER }}>
                          <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{fmtTime(r.esri_completed_at)}</td>
                          <td className="px-3 py-2 font-bold whitespace-nowrap">
                            {r.ticket_link
                              ? <a href={r.ticket_link} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: '#c9a84c' }}>{r.ticket_no}</a>
                              : <span style={{ color: '#c9a84c' }}>{r.ticket_no}</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-200 whitespace-nowrap">{r.city}{r.place && r.place !== r.city && <span className="text-gray-500"> (in {r.place})</span>}</td>
                          <td className="px-3 py-2 text-gray-200">{[r.address, r.street].filter(Boolean).join(' ') || '—'}{r.cross_street && <span className="text-gray-400"> @ {r.cross_street}</span>}</td>
                          <td className={`px-3 py-2 ${r.pm1 ? 'text-white' : 'text-gray-500'}`}>{r.pm1 || '—'}</td>
                          <td className="px-3 py-2 max-w-[14rem]" style={{ color: r.conflict ? YELLOW.text : '#d1d5db' }}>{r.city_status || '—'}</td>
                          <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{r.city_completed_at ? fmtTime(r.city_completed_at) : '—'}</td>
                          <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{fmtDate(r.call_date)}</td>
                          <td className="px-3 py-2 text-gray-400">{r.excavator || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </ReportShell>
  );
}

const total = (o) => Object.values(o).reduce((a, b) => a + b, 0);

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border px-3 py-2" style={{ borderColor: BORDER, backgroundColor: '#080f1a' }}>
      <div className="text-[11px] text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-black text-white tabular-nums">{value}</div>
    </div>
  );
}
